/**
 * ThemeProvider — owns the resolved theme + writes `[data-theme]` on
 * `<html>` so the `@nu-assist/ui` token vars cascade (the dark default
 * lives in `:root`, the light override under `[data-theme="light"]`).
 *
 * Lives in `@nu-assist/shared-fe` (NOT `@nu-assist/ui`) because theme is
 * state/behavior, and `shared-fe` may depend on `ui` while `ui` keeps
 * zero internal deps (epic decision D7; resolves the 2026-05-12 open
 * decision).
 *
 * Resolution order (dark-first policy — post-manual-test revision,
 * user-approved 2026-05-19):
 *   1. persisted explicit choice (`localStorage`) — user intent wins
 *   2. otherwise ALWAYS dark
 *
 * This is a DARK-FIRST product: dark is the default everywhere; light
 * is opt-in via the in-app toggle only. The OS color-scheme signal is
 * deliberately NOT consulted — this SUPERSEDES the earlier OS-aware
 * resolution decision (D7 / WS2) per explicit user direction on
 * 2026-05-19 (manual testing showed a light-mode OS incorrectly
 * forcing the web app light on first load).
 *
 * Storage is guarded — a private-mode browser (throwing `localStorage`)
 * or a non-DOM test env degrades to the in-memory default (dark),
 * never throws.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Theme = "dark" | "light";

export interface ThemeContextValue {
  /** The currently applied theme. */
  theme: Theme;
  /** Flip dark ↔ light and persist the choice. */
  toggle: () => void;
  /** Set an explicit theme and persist it. */
  set: (next: Theme) => void;
}

const STORAGE_KEY = "nu-assist:theme";
const FALLBACK_THEME: Theme = "dark";

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Read the persisted choice, guarded (storage may throw / be absent). */
function readStored(): Theme | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    return raw === "dark" || raw === "light" ? raw : null;
  } catch {
    return null;
  }
}

/** Best-effort persist (private-mode browsers throw on `setItem`). */
function writeStored(theme: Theme): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, theme);
  } catch {
    // A lost theme preference is non-fatal — swallow and move on.
  }
}

/**
 * Resolve the initial theme: a persisted explicit choice wins;
 * otherwise ALWAYS dark. The OS color-scheme signal is intentionally
 * NOT consulted (dark-first product; light is opt-in via the toggle
 * only — see the file docstring for the 2026-05-19 revision rationale).
 */
function resolveInitial(): Theme {
  return readStored() ?? FALLBACK_THEME;
}

export interface ThemeProviderProps {
  children: ReactNode;
  /**
   * Test/Storybook seam — force the initial theme, skipping
   * storage + system detection. Omit in app code.
   */
  initialTheme?: Theme;
}

export function ThemeProvider({
  children,
  initialTheme,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(
    () => initialTheme ?? resolveInitial(),
  );

  // Reflect onto `<html data-theme>` so the `ui` token vars switch. The
  // dark scope is `:root` (default), light is `[data-theme="light"]`;
  // we set the attribute explicitly for both so the value is always
  // unambiguous (and so a Radix `darkMode: [data-theme="dark"]` variant
  // would also resolve).
  useEffect(() => {
    const root = globalThis.document?.documentElement;
    if (!root) return;
    root.setAttribute("data-theme", theme);
  }, [theme]);

  const set = useCallback((next: Theme) => {
    setThemeState(next);
    writeStored(next);
  }, []);

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === "dark" ? "light" : "dark";
      writeStored(next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, toggle, set }),
    [theme, toggle, set],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

/**
 * useTheme — `{ theme, toggle, set }`. Must be called under a
 * `<ThemeProvider>`; throws otherwise so the missing-provider bug
 * surfaces at the call site rather than as a silent no-op.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within a <ThemeProvider>");
  }
  return ctx;
}
